// src/pages/Realisations.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import { ThemeContext } from "./contexts/ThemeContext";
import { NavbarContext } from "./contexts/NavbarContext";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CONTACT, PROFILE, RDV, REALISATIONS, SERVICES, realisationDetail } from "../config/routes";

import "./styles/Realisations.css";

import { projects as projectsData } from "../data/projectsData";

import duoDateCover from "../images/png/achievements/duoDate.png";
import airbusCover from "../images/png/achievements/airbus_logo.png";
import leouCover from "../images/png/achievements/leou.png";
import epopeeVfCover from "../images/png/achievements/velofute.png";
import echoReviewCover from "../images/png/achievements/echoreview.png";
import ilaCover from "../images/png/achievements/ila.png";
import lmpCover from "../images/png/achievements/lmp.png";
import matchAssessCover from "../images/png/achievements/match-assess.png";
import pixelBattleCover from "../images/png/achievements/pixel_battle.png";
import psychiIACover from "../images/png/achievements/psychia.png";
import scoCover from "../images/png/achievements/sco.png";
import traceCover from "../images/png/achievements/trace.png";
import sdrhCover from "../images/png/achievements/sdrh.png";

import etiopatheCover from "../images/png/achievements/etiopathe.png";
import easySchoolCover from "../images/png/achievements/easySchool.png";

const COVER_BY_SLUG = {
  "duo-date": duoDateCover,
  "airbus-helicopters-ce": airbusCover,
  "domaine-du-leou": leouCover,
  "epopee-vf": epopeeVfCover,
  "echo-review": echoReviewCover,
  ila: ilaCover,
  lmp: lmpCover,
  "match-assess": matchAssessCover,
  "pixel-battle": pixelBattleCover,
  psychia: psychiIACover,
  sco: scoCover,
  trace: traceCover,
  sdrh: sdrhCover,
  etiopathe: etiopatheCover,
  "easy-school": easySchoolCover,
};

const STATUS_LABELS = {
  production: "En production",
  mvp: "MVP",
  "in-progress": "En cours",
  prototype: "Prototype",
  "client-project": "Projet client",
};

const TAGS = {
  ALL: "All",
  WEB: "Web",
  SAAS: "SaaS",
  MOBILE: "Mobile",
  GROWTH: "Growth",
  GAME: "Jeu",
  TOOL: "Outil",
  IN_PROGRESS: "En cours",
};

function cn(...x) {
  return x.filter(Boolean).join(" ");
}

function initials(title = "") {
  const parts = title.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase()).join("");
}

function sortByPriority(a, b) {
  const rankA = a.featuredRank ?? a.priority ?? 999;
  const rankB = b.featuredRank ?? b.priority ?? 999;
  if (rankA !== rankB) return rankA - rankB;
  return String(a.title).localeCompare(String(b.title));
}

function prepareProject(project) {
  return {
    ...project,
    displayTitle: project.shortTitle || project.title,
    cover: project.coverImage || COVER_BY_SLUG[project.slug] || null,
    to: realisationDetail(project.slug),
  };
}

function StatusBadge({ status }) {
  if (!status) return null;
  return <span className="statusBadge">{STATUS_LABELS[status] || status}</span>;
}

function StackList({ stack, limit = 5 }) {
  if (!stack?.length) return null;
  return (
    <div className="projectStack">
      {stack.slice(0, limit).map((item) => (
        <span className="chip" key={item}>
          {item}
        </span>
      ))}
    </div>
  );
}

function ProjectMedia({ project, large = false }) {
  return (
    <div className={cn("projectMedia", large && "projectMedia--large")}>
      {project.cover ? (
        <img src={project.cover} alt={project.title} loading="lazy" />
      ) : (
        <div className="projectPlaceholder" aria-hidden="true">
          <span>{initials(project.displayTitle)}</span>
        </div>
      )}
    </div>
  );
}

function FeaturedProjectCard({ project }) {
  const buttonLabel = project.hasDetailedCaseStudy ? "Voir l'etude de cas" : "Voir la fiche";
  const roleItems = (project.role || []).slice(0, 3);

  return (
    <article className="featuredProject" id={project.slug}>
      <ProjectMedia project={project} large />

      <div className="featuredProject-body">
        <div className="projectMetaLine">
          <span className="projectType">{project.type}</span>
          <StatusBadge status={project.status} />
        </div>

        <h3 className="featuredProject-title">{project.displayTitle}</h3>
        <p className="featuredProject-summary">{project.summary || project.subtitle}</p>

        {project.technicalSummary ? (
          <p className="featuredProject-tech">{project.technicalSummary}</p>
        ) : null}

        {roleItems.length ? (
          <ul className="projectRoleList">
            {roleItems.map((item) => (
              <li key={`${project.slug}-${item}`}>{item}</li>
            ))}
          </ul>
        ) : null}

        <StackList stack={project.stack} />

        <div className="projectActions">
          <Link to={project.to} className="btn btn-primary">
            {buttonLabel}
          </Link>
        </div>
      </div>
    </article>
  );
}

function ProjectCard({ project, compact = false }) {
  const buttonLabel = project.hasDetailedCaseStudy ? "Voir le projet" : "Voir la fiche";

  return (
    <article className={cn("projectCard", compact && "projectCard--compact")} id={project.slug}>
      <ProjectMedia project={project} />

      <div className="projectCard-body">
        <div className="projectMetaLine">
          <span className="projectType">{project.type}</span>
          <StatusBadge status={project.status} />
        </div>

        <h3 className="projectCard-title">{project.displayTitle}</h3>
        <p className="projectCard-summary">{project.summary || project.subtitle}</p>

        <StackList stack={project.stack} limit={compact ? 4 : 5} />

        {project.sensitiveNote ? (
          <p className="projectSensitiveNote">{project.sensitiveNote}</p>
        ) : null}

        <div className="projectActions">
          <Link to={project.to} className="btn btn-tertiary">
            {buttonLabel}
          </Link>
        </div>
      </div>
    </article>
  );
}

function SectionHeader({ eyebrow, title, children }) {
  return (
    <div className="sectionHeader">
      <p className="reals-eyebrow">{eyebrow}</p>
      <h2 className="reals-h2">{title}</h2>
      {children ? <p className="reals-muted">{children}</p> : null}
    </div>
  );
}

export default function Realisations() {
  const { darkMode } = useContext(ThemeContext);
  const { navbarActive, setNavbarActive, scrollPosition } =
    useContext(NavbarContext);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 599);
  const [activeTag, setActiveTag] = useState(TAGS.ALL);
  const [q, setQ] = useState("");

  const callRoute = RDV;
  const metaTitle = "Réalisations web, SaaS & mobile - Dreamer Agency";
  const metaDesc =
    "Découvrez une sélection de projets web, SaaS et applications mobiles réalisés par Dreamer Agency : plateformes métier, API, IA, paiement, géolocalisation et solutions sur mesure.";
  const ogTitle = "Réalisations - SaaS, applications web & mobile";
  const ogDescription =
    "Sélection de projets réalisés : applications SaaS, plateformes métier, mobile, API, IA, paiements, géolocalisation et intégrations tierces.";
  const canonicalUrl = `https://dreameragency.fr${REALISATIONS}`;
  const ogImage = "https://dreameragency.fr/images/og/realisations-dreamer-agency.jpg";

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 599);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const preventTouchMove = (e) => e.preventDefault();
    if (navbarActive && isMobile) {
      document.body.addEventListener("touchmove", preventTouchMove, {
        passive: false,
      });
    } else {
      document.body.removeEventListener("touchmove", preventTouchMove);
    }
    return () =>
      document.body.removeEventListener("touchmove", preventTouchMove);
  }, [navbarActive, isMobile]);

  useEffect(() => {
    const mainContainer = document.querySelector(".reals");
    if (!mainContainer) return;
    if (navbarActive) {
      mainContainer.classList.add("menu-open");
      document.body.classList.add("menu-open");
    } else {
      mainContainer.classList.remove("menu-open");
      document.body.classList.remove("menu-open");
    }
  }, [navbarActive]);

  useEffect(() => {
    if (!navbarActive) window.scrollTo(0, scrollPosition);
  }, [navbarActive, scrollPosition]);

  const handleMainContainerClick = () => {
    if (navbarActive && isMobile) setNavbarActive(false);
  };

  const COVER_BY_SLUG = useMemo(
    () => ({
      "duo-date": duoDateCover,
      "airbus-helicopters-ce": airbusCover,
      "domaine-du-leou": leouCover,
      "epopee-vf": epopeeVfCover,
      "echo-review": echoReviewCover,
      "ila": ilaCover,
      "lmp": lmpCover,
      "match-assess": matchAssessCover,
      "pixel-battle": pixelBattleCover,
      "psychia": psychiIACover,
      "sco": scoCover,
      "trace": traceCover,
      "sdrh": sdrhCover,
    }),
    []
  );

  const baseProjects = useMemo(() => {
    return projectsData.map((p) => ({
      id: p.slug,
      slug: p.slug,
      title: p.title,
      subtitle: p.subtitle,
      tags: p.tags || [],
      stack: p.stack || [],
      featured: !!p.featured,
      cover: COVER_BY_SLUG[p.slug],
      to: realisationDetail(p.slug),
    }));
  }, [COVER_BY_SLUG]);

  const legacyProjects = useMemo(
    () => [
      {
        id: "etiopathe",
        slug: "etiopathe",
        title: "Étiopathe",
        subtitle: "Site vitrine WordPress (5 pages)",
        tags: [TAGS.WEB],
        stack: ["WordPress"],
        featured: false,
        cover: etiopatheCover,
        to: realisationDetail("etiopathe"),
      },
      {
        id: "easy-school",
        slug: "easy-school",
        title: "EasySchool",
        subtitle: "Projet web (détails sur demande)",
        tags: [TAGS.WEB],
        stack: ["Web"],
        featured: false,
        cover: easySchoolCover,
        to: realisationDetail("easy-school"),
      },
      {
        id: "study-leveling",
        slug: "study-leveling",
        title: "Study Leveling",
        subtitle: "App RPG focus (en cours)",
        tags: [TAGS.MOBILE, TAGS.IN_PROGRESS],
        stack: ["Flutter", "Firebase"],
        featured: false,
        cover: null,
        to: callRoute,
      },
    ],
    [callRoute]
  );

  const projects = useMemo(
    () => [...baseProjects, ...legacyProjects],
    [baseProjects, legacyProjects]
  );

  const filters = useMemo(
    () => [
      TAGS.ALL,
      TAGS.WEB,
      TAGS.SAAS,
      TAGS.MOBILE,
      TAGS.GROWTH,
      TAGS.GAME,
      TAGS.TOOL,
      TAGS.IN_PROGRESS,
    ],
    []
  );

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();

    return projects
      .filter((p) => {
        if (activeTag === TAGS.ALL) return true;
        return p.tags?.includes(activeTag);
      })
      .filter((p) => {
        if (!query) return true;
        const hay = [p.title, p.subtitle, ...(p.tags || []), ...(p.stack || [])]
          .join(" ")
          .toLowerCase();
        return hay.includes(query);
      })
      .sort((a, b) => {
        const f = Number(b.featured) - Number(a.featured);
        if (f !== 0) return f;
        return String(a.title).localeCompare(String(b.title));
      });
  }, [projects, activeTag, q]);

  const visibleProjects = useMemo(
    () => projectsData.filter((project) => project.isPublic !== false).map(prepareProject),
    []
  );

  const featuredProjects = useMemo(
    () => visibleProjects.filter((project) => project.section === "featured").sort(sortByPriority),
    [visibleProjects]
  );

  const appProjects = useMemo(
    () => visibleProjects.filter((project) => project.section === "apps").sort(sortByPriority),
    [visibleProjects]
  );

  const clientProjects = useMemo(
    () => visibleProjects.filter((project) => project.section === "clients").sort(sortByPriority),
    [visibleProjects]
  );

  const archiveProjects = useMemo(
    () => visibleProjects.filter((project) => project.section === "archive").sort(sortByPriority),
    [visibleProjects]
  );

  const jsonLd = useMemo(() => {
    const itemList = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Réalisations Dreamer Agency",
      itemListElement: visibleProjects.map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: p.title,
        url: `https://dreameragency.fr${p.to}`,
      })),
    };
    return [itemList];
  }, [visibleProjects]);

  return (
    <main
      className={`reals ${darkMode ? "dark" : "light"}`}
      onClick={handleMainContainerClick}
    >
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta property="og:image:alt" content="Réalisations web, SaaS et applications mobiles - Dreamer Agency" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ogTitle} />
        <meta name="twitter:description" content="Applications SaaS, plateformes métier, mobile, API, IA, paiement et géolocalisation." />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>

      <section className="reals-hero">
        <div className="reals-container">
          <p className="reals-eyebrow">Portfolio technique</p>
          <h1 className="reals-h1">Des applications web, SaaS et produits métier construits de bout en bout.</h1>
          <p className="reals-lead">
            Une sélection de produits full-stack, applications mobiles, intégrations IA, automatisations et projets
            clients. La page reste volontairement synthétique : les études de cas détaillées viendront ensuite.
          </p>

          <div className="proofPills" aria-label="Domaines couverts">
            <span>Applications web & SaaS</span>
            <span>Mobile & géolocalisation</span>
            <span>API, IA & automatisations</span>
          </div>

          <div className="reals-heroActions">
            <a href="#projets-phares" className="btn btn-primary">
              Voir les projets phares
            </a>
            <Link to={PROFILE} className="btn btn-secondary">
              Profil développeur
            </Link>
          </div>
        </div>
      </section>

      <section className="reals-section" id="projets-phares">
        <div className="reals-container">
          <SectionHeader eyebrow="Études de cas prioritaires" title="Projets phares">
            Les projets qui montrent le mieux la capacité full-stack : produit, frontend, backend, données, API,
            intégrations et déploiement.
          </SectionHeader>

          <div className="featuredGrid">
            {featuredProjects.map((project) => (
              <FeaturedProjectCard project={project} key={project.slug} />
            ))}
          </div>
        </div>
      </section>

      <section className="reals-section reals-sectionAlt">
        <div className="reals-container">
          <SectionHeader eyebrow="Produits digitaux" title="Applications & produits">
            Des applications mobiles, outils SaaS, expériences temps réel, prototypes IA et produits plus ciblés.
          </SectionHeader>

          <div className="projectGrid">
            {appProjects.map((project) => (
              <ProjectCard project={project} key={project.slug} />
            ))}
          </div>
        </div>
      </section>

      <section className="reals-section">
        <div className="reals-container">
          <SectionHeader eyebrow="Freelance & clients" title="Sites vitrines, SEO & projets clients">
            Des livraisons plus commerciales : présence professionnelle, SEO local, sites vitrines, refontes et
            projets clients à périmètre clair.
          </SectionHeader>

          <div className="projectGrid projectGrid--compact">
            {clientProjects.map((project) => (
              <ProjectCard project={project} compact key={project.slug} />
            ))}
          </div>
        </div>
      </section>

      {archiveProjects.length ? (
        <section className="reals-section reals-sectionArchive">
          <div className="reals-container">
            <SectionHeader eyebrow="Archives" title="Expérimentations / anciens projets">
              Quelques projets gardés comme trace du parcours, sans les mettre au même niveau que les produits actuels.
            </SectionHeader>

            <div className="archiveGrid">
              {archiveProjects.map((project) => (
                <ProjectCard project={project} compact key={project.slug} />
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <section className="reals-finalCta">
        <div className="reals-container finalCtaGrid">
          <div className="finalCtaCard">
            <p className="reals-eyebrow">Recrutement CDI</p>
            <h2>Vous recrutez un développeur full-stack ?</h2>
            <p>
              Consultez le profil de Florian Da Cunha : stack, expérience, posture produit et projets représentatifs.
            </p>
            <Link to={PROFILE} className="btn btn-primary">
              Voir le profil développeur
            </Link>
          </div>

          <div className="finalCtaCard">
            <p className="reals-eyebrow">Projet client</p>
            <h2>Vous avez un projet web, SaaS ou mobile ?</h2>
            <p>
              Parlons du scope, des risques techniques, du délai et de la meilleure manière de livrer une première
              version solide.
            </p>
            <Link to={CONTACT} className="btn btn-secondary">
              Contacter Dreamer Agency
            </Link>
          </div>
        </div>
      </section>

      <section className="reals-bottomLink">
        <Link to={RDV} className="link">
          Ou réserver directement un appel
        </Link>
      </section>

      {false && (
        <>
      <section className="reals-hero">
        <div className="reals-container">
          <p className="reals-eyebrow">Réalisations</p>
          <h1 className="reals-h1">Des projets livrés. Des produits qui tiennent.</h1>
          <p className="reals-lead">
            Sites web premium, SaaS, apps iOS/Android, SEO/Tracking, IA…
            Filtre par type de projet et clique pour voir le détail (ou demande un exemple proche de ton besoin).
          </p>

          <div className="reals-heroActions">
            <Link to={RDV} className="btn btn-primary">Réserver un appel</Link>
            <Link to={SERVICES} className="btn btn-secondary">Voir les services</Link>
            <Link to={CONTACT} className="btn btn-ghost">Contact</Link>
          </div>

          <div className="reals-controls">
            <div className="reals-filters">
              {filters.map((f) => (
                <button
                  key={f}
                  type="button"
                  className={cn("chipBtn", activeTag === f && "active")}
                  onClick={() => setActiveTag(f)}
                >
                  {f}
                </button>
              ))}
            </div>

            <div className="reals-search">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Rechercher : Flutter, SaaS, SEO, Node.js…"
                aria-label="Rechercher un projet"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="reals-section">
        <div className="reals-container">
          <div className="reals-grid">
            {filtered.map((p) => (
              <article key={p.slug} id={p.slug} className="proj">
                <div className="proj-media">
                  {p.cover ? (
                    <img src={p.cover} alt={p.title} loading="lazy" />
                  ) : (
                    <div className="proj-placeholder" aria-hidden="true">
                      <span>{initials(p.title)}</span>
                    </div>
                  )}
                </div>

                <div className="proj-body">
                  <div className="proj-tags">
                    {(p.tags || []).slice(0, 3).map((tag) => (
                      <span className="tag" key={`${p.slug}-${tag}`}>{tag}</span>
                    ))}
                  </div>

                  <h2 className="proj-title">{p.title}</h2>
                  <p className="proj-sub">{p.subtitle}</p>

                  <div className="proj-stack">
                    {(p.stack || []).slice(0, 4).map((s) => (
                      <span className="chip" key={`${p.slug}-${s}`}>{s}</span>
                    ))}
                  </div>

                  <div className="proj-actions">
                    <Link to={p.to} className="btn btn-tertiary">
                      Voir le projet →
                    </Link>
                    <Link to={RDV} className="link">
                      Projet similaire ? →
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>

          <div className="reals-cta">
            <div>
              <h2 className="reals-h2">Tu veux un projet similaire ?</h2>
              <p className="reals-muted">
                Réserve un appel : on te dit l’approche, le scope MVP et une fourchette réaliste.
              </p>
            </div>
            <div className="reals-ctaActions">
              <Link to={RDV} className="btn btn-primary">Réserver un appel</Link>
              <Link to={SERVICES} className="btn btn-ghost">Voir les services</Link>
            </div>
          </div>
        </div>
      </section>

        </>
      )}

      {jsonLd.map((obj, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </main>
  );
}
